import React from 'react'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { cn } from '@/utils'
import { ChevronUp, ChevronDown } from 'lucide-react'

export interface Column<T> {
  key: string
  title: string
  render?: (item: T) => React.ReactNode
  width?: string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (item: T) => string | number
  isLoading?: boolean
  emptyMessage?: string
  onRowClick?: (item: T) => void
  className?: string
  rowClassName?: string | ((item: T) => string)
  sortColumn?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (column: string) => void
}

const DataTable = <T,>({
  columns,
  data,
  keyExtractor,
  isLoading,
  emptyMessage = 'No data available',
  onRowClick,
  className,
  rowClassName,
  sortColumn,
  sortDirection,
  onSort
}: DataTableProps<T>) => {
  const getRowClassName = (item: T) => {
    if (typeof rowClassName === 'function') {
      return rowClassName(item)
    }
    return rowClassName
  }

  return (
    <div className={cn('w-full', className)}>
      <Table>
        <TableHeader>
          <TableRow className="table-header-border hover:bg-transparent">
            {columns.map((column, colIndex) => (
              <TableHead
                key={column.key}
                className={cn(
                  column.width && column.width,
                  column.sortable && 'cursor-pointer',
                  // Для первого столбца padding pl-[16px] pr-[8px] py-[16px]
                  // Для остальных pl-0 pr-[8px] py-[8px]
                  colIndex === 0 
                    ? 'pl-[16px] pr-[8px] py-[16px]' 
                    : 'pl-0 pr-[8px] py-[8px]'
                )}
                onClick={() => column.sortable && onSort?.(column.key)}
              >
                {colIndex === 0 ? (
                  // Первая колонка - без внутренней кнопки, просто текст
                  <span className="font-plus-jakarta font-medium text-[14px] leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    {column.title}
                  </span>
                ) : (
                  // Остальные колонки - с внутренней кнопкой и padding px-4 (16px)
                <div className={cn(
                    "inline-flex items-center gap-2 h-[36px] px-4 py-2 rounded-md",
                    column.align === 'center' ? 'justify-center' :
                    column.align === 'right' ? 'justify-end' :
                    'justify-start' // По умолчанию left-aligned
                )}>
                  <span className="font-plus-jakarta font-medium text-[14px] leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">{column.title}</span>
                  {column.sortable && (
                    <span className="text-muted-foreground flex flex-col items-center justify-center">
                      {sortColumn === column.key ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp size={12} className="inline" />
                        ) : (
                          <ChevronDown size={12} className="inline" />
                        )
                      ) : (
                        <div className="flex flex-col items-center justify-center -space-y-1.5 leading-none">
                          <ChevronUp size={12} className="inline opacity-60" />
                          <ChevronDown size={12} className="inline opacity-60" />
                        </div>
                      )}
                    </span>
                  )}
                </div>
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-8">
                <div className="flex justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                </div>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-8 text-gray-500 dark:text-gray-400">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, index) => (
              <TableRow
                key={keyExtractor(item)}
                className={cn(
                  'transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-accent dark:hover:bg-[rgba(253,253,253,0.1)]',
                  // Чередующийся фон для некоторых ячеек (как в дизайне Figma)
                  index % 2 === 0 && '[&>td]:bg-[rgba(255,255,255,0.28)] dark:[&>td]:bg-[rgba(253,253,253,0.05)]',
                  getRowClassName(item)
                )}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      // Для первого столбца: pl-[16px] pr-[8px] py-[8px]
                      // Для остальных: pl-0 pr-[8px] py-[16px] (16px сверху и снизу для правильного выравнивания badge)
                      colIndex === 0 
                        ? 'pl-[16px] pr-[8px] py-[8px]' 
                        : 'pl-0 pr-[8px] py-[16px]'
                    )}
                  >
                    {colIndex === 0 ? (
                      // Первая колонка - без внутренней обертки, контент сразу
                      <>
                        {column.render
                          ? column.render(item)
                          : (item as any)[column.key]}
                      </>
                    ) : (
                      // Остальные колонки - с внутренней оберткой и padding px-4 (16px)
                      <div className={cn(
                        "flex items-center w-full h-full px-4",
                        column.align === 'center' ? 'justify-center' :
                        column.align === 'right' ? 'justify-end' :
                        'justify-start' // По умолчанию left-aligned
                      )}>
                    {column.render
                      ? column.render(item)
                      : (item as any)[column.key]}
                      </div>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default DataTable 